import { useState } from 'react';
import { X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export type SurveyTrigger = 'place_lookup' | 'navigation';
type SurveyStep = 'welcome' | 'rating' | 'comment' | 'done';

interface FeedbackSurveyProps {
    trigger: SurveyTrigger;
    placeName?: string;
    onDismiss: () => void;
    onSubmit: (rating: number, comment: string) => Promise<void>;
}

export function FeedbackSurvey({ trigger, placeName, onDismiss, onSubmit }: FeedbackSurveyProps) {
    const [step, setStep] = useState<SurveyStep>('welcome');
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const isPlaceLookup = trigger === 'place_lookup';

    const welcomeTitle = isPlaceLookup ? 'Help us improve Coordal Maps!' : 'How was your route?';
    const welcomeSub = isPlaceLookup
        ? `You looked up "${placeName || 'a location'}". Got a minute to share if we got it right?`
        : 'Got a minute to share how your navigation went? Your feedback shapes what we improve next.';
    const ratingQuestion = isPlaceLookup
        ? 'How accurate was this result for what you were looking for?'
        : 'Overall, how helpful was this route?';

    const handleSubmit = async () => {
        setSubmitting(true);
        await onSubmit(rating, comment);
        setStep('done');
        setSubmitting(false);
        setTimeout(onDismiss, 2200);
    };

    return (
        <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 26, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 sm:inset-0 z-[70] flex justify-center sm:items-center"
        >
            <div className="bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-3xl shadow-2xl border-t border-gray-200/60 dark:border-gray-700/60 sm:border px-6 pt-5 pb-8 w-full sm:max-w-md">
                <div className="w-10 h-1 bg-gray-200 dark:bg-gray-700 rounded-full mx-auto mb-5 sm:hidden" />

                <AnimatePresence mode="wait">

                    {step === 'welcome' && (
                        <motion.div key="welcome" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                            <div className="flex items-start justify-between mb-4">
                                <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
                                    <img src="/logos/coordal-logo.svg" alt="Coordal" className="w-7 h-7" />
                                </div>
                                <button
                                    aria-label="Close survey"
                                    onClick={onDismiss}
                                    className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{welcomeTitle}</h2>
                            <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed mb-6">
                                {welcomeSub} Your feedback helps us make addressing in Ghana better for everyone.
                            </p>
                            <button
                                onClick={() => setStep('rating')}
                                className="w-full py-3.5 bg-emerald-500 text-white font-semibold rounded-2xl hover:bg-emerald-600 active:bg-emerald-700 transition-colors"
                            >
                                Start survey
                            </button>
                            <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-3">Takes less than a minute · No sign-in needed</p>
                        </motion.div>
                    )}

                    {step === 'rating' && (
                        <motion.div key="rating" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                            <div className="w-full h-1 bg-gray-100 dark:bg-gray-800 rounded-full mb-5 overflow-hidden">
                                <div className="h-full w-1/2 bg-emerald-500 rounded-full transition-all" />
                            </div>
                            <div className="flex items-start justify-between mb-5">
                                <h2 className="text-lg font-bold text-gray-900 dark:text-white flex-1 pr-4 leading-snug">
                                    {ratingQuestion}
                                </h2>
                                <button aria-label="Close survey" onClick={onDismiss} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500 mb-2 px-0.5">
                                <span>Very poor</span>
                                <span>Excellent</span>
                            </div>
                            <div className="flex items-center gap-2 mb-1">
                                {[1, 2, 3, 4, 5].map(n => (
                                    <button
                                        key={n}
                                        onClick={() => setRating(n)}
                                        className={`flex-1 py-3.5 rounded-xl border-2 text-sm font-semibold transition-all ${rating === n
                                            ? 'bg-emerald-500 border-emerald-500 text-white scale-105 shadow-sm'
                                            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-emerald-300 dark:hover:border-emerald-600'
                                            }`}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                            <p className="text-center text-xs text-gray-400 dark:text-gray-500 mb-6">Your feedback helps us improve</p>

                            <button
                                onClick={() => setStep('comment')}
                                disabled={rating === 0}
                                className="w-full py-3.5 bg-emerald-500 text-white font-semibold rounded-2xl hover:bg-emerald-600 active:bg-emerald-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                            >
                                Next
                            </button>
                        </motion.div>
                    )}

                    {step === 'comment' && (
                        <motion.div key="comment" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                            <div className="w-full h-1 bg-gray-100 dark:bg-gray-800 rounded-full mb-5 overflow-hidden">
                                <div className="h-full w-full bg-emerald-500 rounded-full" />
                            </div>
                            <div className="flex items-start justify-between mb-3">
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">We appreciate your feedback!</h2>
                                <button aria-label="Close survey" onClick={onDismiss} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                                    <X size={18} />
                                </button>
                            </div>
                            <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">It helps us improve Coordal Maps for everyone.</p>
                            <textarea
                                value={comment}
                                onChange={e => setComment(e.target.value)}
                                placeholder="Share additional information (optional)"
                                rows={4}
                                className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/30 mb-4"
                            />
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setStep('rating')}
                                    className="flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 font-semibold rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                    className="flex-1 py-3 bg-emerald-500 text-white font-semibold rounded-2xl hover:bg-emerald-600 active:bg-emerald-700 disabled:opacity-50 transition-colors"
                                >
                                    {submitting ? 'Submitting…' : 'Submit'}
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {step === 'done' && (
                        <motion.div
                            key="done"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center py-6"
                        >
                            <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                                <Check className="w-7 h-7 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Thank you!</h2>
                            <p className="text-gray-500 dark:text-gray-400 text-sm">
                                Your feedback makes Coordal Maps better for everyone in Ghana.
                            </p>
                        </motion.div>
                    )}

                </AnimatePresence>
            </div>
        </motion.div>
    );
}
